/**
 * Per-domain API client for the Teams & Groups vertical. Wraps the shared
 * {@link apiFetch} helper so the SPA and API stay same-origin (session cookie
 * is first-party). DTO types come from @fc/core.
 */
import type {
  AddGroupMemberInput,
  Group,
  GroupMember,
  GroupVisibility,
  NewGroupInput,
  NewTeamInput,
  Team,
  TeamNode,
  UpdateGroupInput,
  UpdateGroupMemberInput,
  UpdateTeamInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  AddGroupMemberInput,
  Group,
  GroupEnrollment,
  GroupMember,
  GroupMemberRole,
  GroupMemberStatus,
  GroupVisibility,
  NewGroupInput,
  NewTeamInput,
  Team,
  TeamNode,
  UpdateGroupInput,
  UpdateGroupMemberInput,
  UpdateTeamInput,
} from '@fc/core'

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v) search.set(k, v)
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}

// ---- Teams ----
export const listTeams = (filter?: { siteId?: string; parentTeamId?: string }) =>
  apiFetch<Team[]>(`/teams${qs({ siteId: filter?.siteId, parentTeamId: filter?.parentTeamId })}`)

export const getTeamHierarchy = () => apiFetch<TeamNode[]>('/teams/hierarchy')

export const getTeam = (id: string) => apiFetch<Team>(`/teams/${id}`)

export const createTeam = (input: NewTeamInput) =>
  apiFetch<Team>('/teams', { method: 'POST', body: JSON.stringify(input) })

export const updateTeam = (id: string, input: UpdateTeamInput) =>
  apiFetch<Team>(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

export const deleteTeam = (id: string) => apiFetch<void>(`/teams/${id}`, { method: 'DELETE' })

// ---- Groups ----
export const listGroups = (filter?: {
  teamId?: string
  siteId?: string
  visibility?: GroupVisibility
}) =>
  apiFetch<Group[]>(
    `/groups${qs({ teamId: filter?.teamId, siteId: filter?.siteId, visibility: filter?.visibility })}`,
  )

export const getGroup = (id: string) => apiFetch<Group>(`/groups/${id}`)

export const createGroup = (input: NewGroupInput) =>
  apiFetch<Group>('/groups', { method: 'POST', body: JSON.stringify(input) })

export const updateGroup = (id: string, input: UpdateGroupInput) =>
  apiFetch<Group>(`/groups/${id}`, { method: 'PATCH', body: JSON.stringify(input) })

export const deleteGroup = (id: string) => apiFetch<void>(`/groups/${id}`, { method: 'DELETE' })

// ---- Group members ----
export const listGroupMembers = (groupId: string) =>
  apiFetch<GroupMember[]>(`/groups/${groupId}/members`)

export const addGroupMember = (groupId: string, input: AddGroupMemberInput) =>
  apiFetch<GroupMember>(`/groups/${groupId}/members`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateGroupMember = (
  groupId: string,
  memberId: string,
  input: UpdateGroupMemberInput,
) =>
  apiFetch<GroupMember>(`/groups/${groupId}/members/${memberId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const removeGroupMember = (groupId: string, memberId: string) =>
  apiFetch<void>(`/groups/${groupId}/members/${memberId}`, { method: 'DELETE' })
